import type { LensFilterValues } from '~/types/leLens';

import { growth, num, ratio } from './cards';

export const DAILY_BOARD_PAGE_ID = 'daily-board';

export const BOARD_VIEWS = [
  { value: 'daily', label: 'Daily Sales Breakdown Results' },
  { value: 'monthly', label: 'Sales Breakdown Results by Transaction Date' },
  { value: 'vertical', label: 'Monthly Breakdown Results by Vertical' },
  { value: 'region', label: 'Sales Breakdown Results by Deal Region' },
  { value: 'items', label: 'Items Breakdown Results by Deal Region' },
  { value: 'attach', label: 'Attachment Rates' },
  { value: 'supplier', label: 'Net Sales by Supplier' },
  { value: 'supply', label: 'Results by Point of Supply' },
  { value: 'sale', label: 'Results by Point of Sale' },
  { value: 'refunds', label: 'Refunds Tracking' },
] as const;
export type BoardView = (typeof BOARD_VIEWS)[number]['value'];

export const SALES_PERIODS = ['Yesterday', 'Last 7 Days', 'MTD', 'QTD', 'YTD'] as const;
export type SalesPeriod = (typeof SALES_PERIODS)[number];
export const YEAR_TYPES = ['Calendar Year', 'Financial Year'] as const;
export type YearType = (typeof YEAR_TYPES)[number];
/** Board categories as analytics.sales_with_refunds names them (board_report_product_name). */
export const PRODUCT_CATEGORIES = [
  'All',
  'Accommodation',
  'Tours',
  'Cruises',
  'Flights',
  'Charter Flights',
  'Experiences',
  'Insurance',
  'LuxPlus+',
] as const;

export const BOARD_SELECTS = [
  { key: 'brand', label: 'Brand' },
  { key: 'deal_region', label: 'Deal region' },
  { key: 'customer_country', label: 'Point of sale' },
  { key: 'offer_country', label: 'Point of supply' },
  { key: 'sales_currency', label: 'Sales currency' },
  { key: 'supplier', label: 'Supplier' },
] as const;
type BoardSelectKey = (typeof BOARD_SELECTS)[number]['key'];

export type BoardFilters = {
  view: BoardView;
  salesPeriod: SalesPeriod;
  yearType: YearType;
  productCategory: string;
  includeFlights: boolean;
  removeAddonMigration: boolean;
  selections: Record<BoardSelectKey, Array<string>>;
};

const pick = <T extends string>(values: ReadonlyArray<T>, value: string | null, fallback: T): T =>
  values.includes(value as T) ? (value as T) : fallback;

/** Workbook defaults: MTD, Calendar Year, All categories, flights excluded, addon migration removed. */
export function parseBoardFilters(search: URLSearchParams): BoardFilters {
  return {
    view: pick(
      BOARD_VIEWS.map((view) => view.value),
      search.get('tab'),
      'daily',
    ),
    salesPeriod: pick(SALES_PERIODS, search.get('salesPeriod'), 'MTD'),
    yearType: pick(YEAR_TYPES, search.get('yearType'), 'Calendar Year'),
    productCategory: search.get('productCategory') ?? 'All',
    includeFlights: search.get('includeFlights') === 'yes',
    removeAddonMigration: search.get('removeAddonMigration') !== 'no',
    selections: Object.fromEntries(
      BOARD_SELECTS.map(({ key }) => [key, search.getAll(key)]),
    ) as BoardFilters['selections'],
  };
}

/** Back to the workbook defaults, keeping the open tab. */
export function resetBoardFilters(filters: BoardFilters): BoardFilters {
  return { ...parseBoardFilters(new URLSearchParams()), view: filters.view };
}

export function writeBoardFilters(filters: BoardFilters, search: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(search);
  next.set('tab', filters.view);
  next.set('salesPeriod', filters.salesPeriod);
  next.set('yearType', filters.yearType);
  next.set('productCategory', filters.productCategory);
  next.set('includeFlights', filters.includeFlights ? 'yes' : 'no');
  next.set('removeAddonMigration', filters.removeAddonMigration ? 'yes' : 'no');
  for (const { key } of BOARD_SELECTS) {
    next.delete(key);
    filters.selections[key].forEach((value) => next.append(key, value));
  }
  return next;
}

export function salesPeriodWindow(period: SalesPeriod, yearType: YearType) {
  switch (period) {
    case 'Yesterday':
      return { days: 1 };
    case 'Last 7 Days':
      return { days: 7 };
    case 'QTD':
      // Fiscal quarters start in Jan/Apr/Jul/Oct too, so QTD is the same under either year type.
      return { toDate: 'quarter' };
    case 'YTD':
      return { toDate: yearType === 'Financial Year' ? 'fiscalYear' : 'year' };
    default:
      return { toDate: 'month' };
  }
}

export function boardRequest(filters: BoardFilters): LensFilterValues {
  const values: LensFilterValues = {
    period: {
      anchor: 'yesterday',
      window: salesPeriodWindow(filters.salesPeriod, filters.yearType),
      comparison: 'previous',
    },
  } as LensFilterValues;
  if (filters.productCategory !== 'All') {
    values.product_category = [filters.productCategory];
  }
  if (!filters.includeFlights) {
    values.is_flight = ['No'];
  }
  if (filters.removeAddonMigration) {
    values.addon_migration = ['No'];
  }
  for (const { key } of BOARD_SELECTS) {
    if (filters.selections[key].length) {
      values[key] = filters.selections[key];
    }
  }
  return values;
}

const TOTALS = {
  grossSales: 'gross_sales_aud',
  refunds: 'refunds_aud',
  netSales: 'net_sales_aud',
  margin: 'net_margin_aud',
  orders: 'orders',
  customers: 'customers',
  aovSales: 'aov_sales_aud',
  aovOrders: 'aov_orders',
  items: 'items',
  leadtimeTotal: 'leadtime_days_total',
  leadtimeKnown: 'leadtime_known',
} as const;
type BoardTotals = Record<keyof typeof TOTALS, number | null>;

export function totalsOf(row: Record<string, unknown> | undefined): BoardTotals {
  return Object.fromEntries(Object.entries(TOTALS).map(([key, column]) => [key, num(row?.[column])])) as BoardTotals;
}

/** Ratios of sums and distinct counts from the same scope, never an average of ratios. */
export function ratesOf(totals: BoardTotals) {
  return {
    // As in the workbook, AOV leaves out surcharge and service-fee lines.
    averageOrderValue: ratio(totals.aovSales, totals.aovOrders),
    itemsPerBasket: ratio(totals.items, totals.orders),
    leadtime: ratio(totals.leadtimeTotal, totals.leadtimeKnown),
  };
}

/** The workbook's month-over-month growth: each bucket against the one before it. */
export function sequentialGrowth(values: ReadonlyArray<{ key: string; value: number | null }>) {
  return values.map((entry, index) => ({
    ...entry,
    growth: index ? growth(entry.value, values[index - 1].value) : null,
  }));
}
